import React from 'react';
import { useTranslation } from 'react-i18next';
import InfoPage from '../../InfoPage';

export default function PrivacyPolicy() {
  const { t } = useTranslation();
  return (
    <InfoPage
      eyebrow={t('info.legalEyebrow')}
      title={t('legalPages.privacy.title')}
      subtitle={t('legalPages.privacy.subtitle')}
    >
      {/* Legal text is deliberately NOT machine-translated: a translation
          nobody has legally reviewed would carry meaning no one signed off
          on. The chrome localizes; the binding text stays English, and says
          so in the reader's language. */}
      <p className="info-english-notice"><em>{t('info.englishNotice')}</em></p>
      <p className="info-updated">Last updated: January 2026</p>

      <h2>Overview</h2>
      <p>
        StarView Cinema ("StarView," "we," "us") operates the StarView Cinema mobile app and website
        (collectively, the "Services"). This policy explains what personal information we collect when you use
        the Services, how we use it, who we share it with, and the choices you have about it.
      </p>

      <h2>Information We Collect</h2>
      <h3>Information you give us</h3>
      <ul>
        <li>Account details: name, email address, password (stored as a salted hash, never in plain text), and country.</li>
        <li>Payment details: processed directly by Stripe - we never see or store your full card number.</li>
        <li>Order details: delivery address and special instructions when you use OrderUp in-seat delivery.</li>
        <li>Support messages: anything you send our support assistant or a human agent.</li>
        <li>Driver applications: government ID, driver's license, vehicle registration, insurance, and a verification selfie, if you apply to drive.</li>
      </ul>
      <h3>Information collected automatically</h3>
      <ul>
        <li>Device and usage data: app version, device type, and general interaction data used to keep the Services working and to improve them.</li>
        <li>Approximate location: derived from your IP address, used to default your language and nearby theater - never precise GPS location unless you separately grant it for delivery tracking.</li>
        <li>Ticket and viewing activity: which showtimes you book and which trailers play through so we can show real availability and, in aggregate, report trailer performance to studios.</li>
      </ul>

      <h2>How We Use Your Information</h2>
      <ul>
        <li>To create and secure your account, and to process ticket and food orders.</li>
        <li>To show you real seat availability and prevent two people from being sold the same seat.</li>
        <li>To operate Galaxies rewards, StarView X membership, and promotional offers.</li>
        <li>To communicate with you about your orders, account, and (only with your consent) marketing offers.</li>
        <li>To review and approve driver applications.</li>
        <li>To detect and prevent fraud, abuse, and security incidents.</li>
      </ul>

      <h2>Who We Share It With</h2>
      <p>We share personal information only where genuinely necessary to run the Services:</p>
      <ul>
        <li><strong>Payment processing:</strong> Stripe, and select local payment processors in specific markets.</li>
        <li><strong>Delivery:</strong> the independent OrderUp driver assigned to your order sees your delivery address and order contents, not your payment details.</li>
        <li><strong>Studios:</strong> aggregate, de-identified trailer performance statistics (view counts, completion rates) - never tied to an individual account.</li>
        <li><strong>Legal requirements:</strong> where required to comply with a valid legal process.</li>
      </ul>
      <p>We do not sell your personal information.</p>

      <h2>Your Choices</h2>
      <ul>
        <li>You can review and update most account information directly in the app under Account &gt; Settings.</li>
        <li>You can unsubscribe from marketing email at any time using the link in the email, or from the footer newsletter form on this site.</li>
        <li>You can request a copy of, or deletion of, your personal information by contacting us (see below). Residents of California have additional rights - see our California Collection Notice.</li>
      </ul>

      <h2>Data Retention</h2>
      <p>
        We keep account and ticket records for as long as your account is active and for a reasonable period
        afterward for accounting, fraud-prevention, and legal purposes. Driver application documents are retained
        only as long as needed for review and, if approved, for the duration of the driver's active status.
      </p>

      <h2>Security</h2>
      <p>
        Passwords are hashed, payment data never touches our own servers, and access to sensitive records (like
        driver documents) is restricted to authorized reviewers. No system is perfectly secure - see our
        Vulnerability Reporting page if you believe you've found an issue.
      </p>

      <h2>Contact Us</h2>
      <p>
        Questions about this policy or your data can be sent to{' '}
        <a href="mailto:privacy@starviewcinema.com">privacy@starviewcinema.com</a>.
      </p>
    </InfoPage>
  );
}
